"use client";

import { format } from "date-fns";
import Image from "next/image";
import Link from "next/link";

import { cn } from "@/lib/utils";

export interface MarketingListItem {
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  category?: string;
  cover?: string;
  coverAlt?: string;
}

interface MarketingEntryListProps {
  items: MarketingListItem[];
  categories: string[];
  categoryCounts: Record<string, number>;
  totalCount: number;
  // Section the list belongs to, e.g. "/blog" or "/whats-new". Drives every
  // link target (entries, the all-items pill, and category pages).
  basePath: string;
  // Sidebar label for the unfiltered view, e.g. "All Posts" / "All Updates".
  allLabel: string;
  // CTA under the featured item, e.g. "Read the post" / "Read the update".
  readMoreLabel: string;
  // Shown in the meta row when an item has no category (blog uses "Blog").
  categoryFallback?: string;
  currentCategory?: string;
  // The hero treatment only fits the main index ("newest on top"). Filtered
  // category views render every item as a uniform row instead.
  showFeatured?: boolean;
  // Whether `${basePath}/topic/[category]` exists. The rail links there, so a
  // section without those routes must opt out or it renders links that 404.
  enableCategoryPages?: boolean;
}

const formatCount = (n: number) => String(n).padStart(2, "0");

function EntryMeta({
  category,
  publishedAt,
  fallback,
}: {
  category?: string;
  publishedAt: string;
  fallback?: string;
}) {
  return (
    <div className="text-marketing-text-muted flex items-center gap-3 font-mono text-xs tracking-[0.09em] uppercase dark:text-neutral-400">
      <span className="inline-flex items-center gap-2">
        <span className="bg-marketing-blue dark:bg-marketing-blue-light size-1.5 rounded-full" />
        {category || fallback}
      </span>
      <time dateTime={publishedAt}>
        {format(new Date(publishedAt), "MMM d, yyyy")}
      </time>
    </div>
  );
}

function ReadMoreArrow() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="transition-transform duration-200 group-hover:translate-x-1"
    >
      <path
        d="M3 8h9M8 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function MarketingEntryList({
  items,
  categories,
  categoryCounts,
  totalCount,
  basePath,
  allLabel,
  readMoreLabel,
  categoryFallback,
  currentCategory,
  showFeatured = true,
  enableCategoryPages = true,
}: MarketingEntryListProps) {
  const featured = showFeatured ? items[0] : undefined;
  const rest = showFeatured ? items.slice(1) : items;
  // A lone category filters nothing: every entry carries it. What's New is in
  // that state (one "Feature" category), and rendering the rail there implies a
  // taxonomy the section does not have. It returns on its own once a second
  // category appears.
  const showCategoryRail = enableCategoryPages && categories.length > 1;

  return (
    <div
      className={cn(
        "grid gap-10",
        showCategoryRail && "lg:grid-cols-[180px_1fr] lg:gap-20",
      )}
    >
      {/* Sidebar */}
      <aside
        className={cn(
          "min-w-0 lg:sticky lg:top-28 lg:self-start",
          !showCategoryRail && "hidden",
        )}
      >
        <p className="text-marketing-text-secondary mb-3 font-mono text-xs tracking-[0.12em] uppercase lg:mb-4 dark:text-neutral-500">
          Categories
        </p>
        <nav className="scrollbar-hide -mx-6 flex flex-row gap-x-6 overflow-x-auto px-6 lg:mx-0 lg:flex-col lg:gap-0 lg:overflow-visible lg:px-0">
          <Link
            href={basePath}
            className={cn(
              "flex shrink-0 items-center gap-2 py-1.5 text-sm whitespace-nowrap transition-colors lg:justify-between lg:gap-3",
              !currentCategory
                ? "text-foreground font-medium"
                : "text-marketing-text-muted hover:text-foreground dark:text-neutral-400",
            )}
          >
            <span>{allLabel}</span>
            <span
              className={cn(
                "hidden font-mono text-xs lg:inline",
                !currentCategory
                  ? "text-marketing-blue dark:text-marketing-blue-light"
                  : "text-marketing-text-secondary dark:text-neutral-500",
              )}
            >
              {formatCount(totalCount)}
            </span>
          </Link>
          {categories.map((category) => {
            const isActive =
              currentCategory?.toLowerCase() === category.toLowerCase();
            return (
              <Link
                key={category}
                href={`${basePath}/topic/${category.toLowerCase()}`}
                className={cn(
                  "flex shrink-0 items-center gap-2 py-1.5 text-sm whitespace-nowrap transition-colors lg:justify-between lg:gap-3",
                  isActive
                    ? "text-foreground font-medium"
                    : "text-marketing-text-muted hover:text-foreground dark:text-neutral-400",
                )}
              >
                <span>{category}</span>
                <span
                  className={cn(
                    "hidden font-mono text-xs lg:inline",
                    isActive
                      ? "text-marketing-blue dark:text-marketing-blue-light"
                      : "text-marketing-text-secondary dark:text-neutral-500",
                  )}
                >
                  {formatCount(categoryCounts[category] ?? 0)}
                </span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Items */}
      <div className="min-w-0">
        {featured && (
          <Link
            href={`${basePath}/${featured.slug}`}
            className="group grid items-center gap-8 md:grid-cols-[1.05fr_1fr] md:gap-11"
          >
            <div className="bg-marketing-blue relative aspect-[16/9] overflow-hidden rounded-xl">
              {featured.cover && (
                <Image
                  src={featured.cover}
                  alt={featured.coverAlt || featured.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 600px"
                  priority
                />
              )}
            </div>
            <div>
              <div className="mb-4">
                <EntryMeta
                  category={featured.category}
                  publishedAt={featured.publishedAt}
                  fallback={categoryFallback}
                />
              </div>
              <h2 className="text-foreground group-hover:text-marketing-blue-dark dark:group-hover:text-marketing-blue-light text-2xl! leading-tight! font-semibold! tracking-tight! transition-colors sm:text-3xl!">
                {featured.title}
              </h2>
              <p className="text-marketing-text-muted mt-4 max-w-prose text-base leading-relaxed dark:text-neutral-400">
                {featured.description}
              </p>
              <span className="text-marketing-blue-dark dark:text-marketing-blue-light mt-6 inline-flex items-center gap-2 text-sm font-medium">
                {readMoreLabel}
                <ReadMoreArrow />
              </span>
            </div>
          </Link>
        )}

        {rest.length > 0 && (
          <div className={cn("flex flex-col", featured && "mt-12")}>
            {rest.map((item, index) => (
              <Link
                key={item.slug}
                href={`${basePath}/${item.slug}`}
                className={cn(
                  "group border-marketing-border-light grid items-center gap-6 py-8 sm:grid-cols-[240px_1fr] lg:grid-cols-[320px_1fr] lg:gap-9 dark:border-white/10",
                  (featured || index > 0) && "border-t",
                )}
              >
                <div className="bg-marketing-blue relative aspect-[16/9] overflow-hidden rounded-lg">
                  {item.cover && (
                    <Image
                      src={item.cover}
                      alt={item.coverAlt || item.title}
                      fill
                      className="object-cover"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 240px, 320px"
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="mb-2.5">
                    <EntryMeta
                      category={item.category}
                      publishedAt={item.publishedAt}
                      fallback={categoryFallback}
                    />
                  </div>
                  <h3 className="text-foreground group-hover:text-marketing-blue-dark dark:group-hover:text-marketing-blue-light text-[1.375rem]! leading-snug! font-semibold! tracking-tight! transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-marketing-text-muted mt-2 line-clamp-2 text-sm leading-relaxed dark:text-neutral-400">
                    {item.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
